import { useQuery } from '@tanstack/react-query';

import { getCurrentKYCStatusApi } from '@flipster/apis/v1/kyc';
import {
  KycClientStatusEnum,
  KycLevelEnum,
  KycLevelStatusEnum,
  KycLevelType,
  UserKycStatus,
} from '@flipster/apis/v1/kyc/types';
import { User } from '@flipster/apis/v1/user/types';
import { alert } from '@flipster/ui/Alert';

import { useAuth } from '../auth/state';

export type KycWarningStatus = 'RequireNextLevel' | 'InProgress' | 'Resubmit' | 'Rejected' | 'TncAgreementRequired';

const levels = ['LEVEL_0', 'LEVEL_1', 'LEVEL_2', 'LEVEL_3', 'LEVEL_4'] as const;

const levelMap: Record<KycLevelType, number> = {
  LEVEL_0: 0,
  LEVEL_1: 1,
  LEVEL_2: 2,
  LEVEL_3: 3,
  LEVEL_4: 4,
};

export const getNextLevel = (currentLevel: KycLevelType) => {
  if (currentLevel === 'LEVEL_4') return null;
  const currentIndex = levels.indexOf(currentLevel);
  return levels[currentIndex + 1];
};

export const getLevelByNumber = (level: KycLevelType) => {
  return levelMap[level];
};

export const getWarningStatus = (
  user: User | undefined,
  kycInfo: UserKycStatus | undefined,
  isAchievedLevel: boolean,
): KycWarningStatus | undefined => {
  // ref: https://www.notion.so/prextech/Web-onboarding-process-improvement-120eb32ad227804c89d4c9544bcd8c7b?pvs=4#120eb32ad22780b8afd6fa915cd8ee13
  if (!user || !kycInfo) return undefined;

  if (!isAchievedLevel) {
    const lowestNotCompletedLevelStatus = [kycInfo.level2Status, kycInfo.level3Status, kycInfo.level4Status].find(
      (status) => status !== 'COMPLETED',
    );
    // 우선순위대로 정렬되어 있음
    if (lowestNotCompletedLevelStatus === 'REJECTED') {
      return 'Rejected';
    }
    if (lowestNotCompletedLevelStatus === 'RESUBMIT') {
      return 'Resubmit';
    }
    if (kycInfo.tncAgreementRequired) {
      return 'TncAgreementRequired';
    }
    if (lowestNotCompletedLevelStatus === 'IN_REVIEW') {
      return 'InProgress';
    }
    if (lowestNotCompletedLevelStatus === 'NOT_STARTED' || lowestNotCompletedLevelStatus === 'IN_PROGRESS') {
      return 'RequireNextLevel';
    }
  }
};

const kycPlaceholderData: UserKycStatus = {
  currentKYCLevel: KycLevelEnum.Level0,
  desirableKYCLevel: KycLevelEnum.Level4,
  level2Status: KycLevelStatusEnum.NotStarted,
  level3Status: KycLevelStatusEnum.NotStarted,
  level4Status: KycLevelStatusEnum.NotStarted,
  enableLevel2: false,
  enableLevel3: false,
  enableLevel4: false,
  clientStatus: KycClientStatusEnum.None,
  legacyKYCLevel: KycLevelEnum.Level0,
  tncAgreementRequired: false,
};
export function useKYC() {
  const { isLoggedIn, user } = useAuth();

  const {
    isPlaceholderData,
    data: kycInfo,
    refetch,
    isFetched,
  } = useQuery({
    queryKey: ['getCurrentKYCStatusApi'],
    queryFn: () =>
      getCurrentKYCStatusApi()
        .then((res) => res.data)
        .catch((err) => {
          alert.error(err, { onlyAxiosError: true });
          throw err;
        }),
    enabled: isLoggedIn,
    refetchOnMount: true,
    placeholderData: kycPlaceholderData,
  });

  const isAchievedLevel =
    kycInfo!.currentKYCLevel >= kycInfo!.desirableKYCLevel && kycInfo?.clientStatus === KycClientStatusEnum.Active;
  const isRequireKyc = isFetched && !isAchievedLevel;
  const isInGracePeriod = Boolean(kycInfo?.gracePeriod);
  const nextLevel = kycInfo ? getNextLevel(kycInfo.currentKYCLevel) : null;
  const warningStatus = isFetched ? getWarningStatus(user, kycInfo, isAchievedLevel) : undefined;

  return {
    isLoading: isPlaceholderData,
    kycInfo: kycInfo!,
    refetch,
    isAchievedLevel,
    isInGracePeriod,
    isRequireKyc,
    isFetched,
    nextLevel,
    warningStatus,
  };
}
